import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";
const SANS = "'Inter', system-ui, sans-serif";
const ACCENT = '#4f8fff';
const TEAL = '#7dcfff';
const GREEN = '#34d399';

const FEATURES = [
  { icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', label: 'Chat', desc: 'AI-powered dev assistant' },
  { icon: 'M12 2a10 10 0 100 20 10 10 0 000-20z M2 12h20', label: 'Browser', desc: 'Built-in web automation' },
  { icon: 'M4 19.5A2.5 2.5 0 016.5 17H20', label: 'Notebook', desc: 'Computational notebooks' },
  { icon: 'M3 3h18v18H3z M3 9h18 M9 21V9', label: 'Canvas', desc: 'Visual design surface' },
  { icon: 'M12 20V10 M18 20V4 M6 20v-4', label: 'Benchmark', desc: 'Performance testing' },
  { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', label: 'Access Control', desc: 'RBAC & governance' },
];

interface OrgInfo { id: string; slug: string; display_name: string; role: string }

export function LobbyPage() {
  const { user, token, logout } = useAuth();
  const [profile, setProfile] = useState({ displayName: '', role: '', company: '', interest: '' });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch('/api/orgs', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setOrgs((data.orgs || []).filter((o: OrgInfo) => o.id !== 'lobby')))
      .catch(() => {})
      .finally(() => setLoadingOrgs(false));
  }, [token]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(profile),
      });
      setSaved(true);
    } catch {}
    setSaving(false);
  };

  const handleJoinOrg = () => {
    const match = inviteLink.trim().match(/invite\/([a-zA-Z0-9_-]+)/);
    if (match) window.location.hash = `invite/${match[1]}`;
  };

  const handleEnterOrg = async (orgId: string) => {
    localStorage.setItem('opentool_active_org', orgId);
    // Refresh the JWT so it includes the real org memberships
    try {
      const resp = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.token) {
          localStorage.setItem('opentool_token', data.token);
          document.cookie = `ot_session=${data.token}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
        }
      }
    } catch { /* proceed anyway */ }
    window.location.reload();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', overflow: 'auto' }}>
      {/* BG */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 50% at 20% 20%, rgba(79,143,255,0.08), transparent), radial-gradient(ellipse 40% 40% at 80% 80%, rgba(125,207,255,0.05), transparent), radial-gradient(ellipse 50% 30% at 50% 60%, rgba(52,211,153,0.03), transparent)' }} />
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(79,143,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(79,143,255,0.02) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(0,0,0,0.6)' }}>
        <div style={{ fontFamily: FONT, fontSize: 18, fontWeight: 700 }}>
          <span style={{ color: ACCENT }}>{'>'}</span>{' '}
          <span style={{ color: '#e6edf3' }}>tool</span>
          <span style={{ color: ACCENT }}>_os</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#484f58', fontSize: 12, fontFamily: FONT }}>{user?.login}</span>
          <button onClick={logout} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#8b949e', fontSize: 11, fontFamily: FONT, cursor: 'pointer', padding: '6px 14px' }}>sign out</button>
        </div>
      </nav>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 20, marginBottom: 24 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
            <span style={{ color: GREEN, fontSize: 11, fontFamily: FONT }}>authenticated</span>
          </div>
          <h1 style={{ fontFamily: SANS, fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 700, color: '#e6edf3', lineHeight: 1.2, margin: '0 0 16px' }}>
            Welcome to <span style={{ color: '#4f8fff', fontWeight: 700 }}>&gt;</span> <span style={{ color: '#e6edf3', fontWeight: 700 }}>tool</span><span style={{ color: '#4f8fff', fontWeight: 700 }}>_os</span>
          </h1>
          <p style={{ fontFamily: SANS, fontSize: 16, color: '#8b949e', lineHeight: 1.6, maxWidth: 540, margin: '0 auto' }}>
            The open platform for AI-native development. Complete your profile and join an organization to get started.
          </p>
        </div>

        {/* Two-column: Profile + Links */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 48 }}>
          {/* Profile card */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(79,143,255,0.12)', borderRadius: 14, padding: '28px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg, ${ACCENT}, ${TEAL})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontSize: 22, fontWeight: 700, fontFamily: FONT }}>
                {user?.login?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div style={{ color: '#e6edf3', fontSize: 16, fontWeight: 600, fontFamily: SANS }}>{user?.login}</div>
                <div style={{ color: '#484f58', fontSize: 12, fontFamily: FONT }}>github.com/{user?.login}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {([
                { key: 'displayName' as const, label: 'Display Name', placeholder: 'How should we address you?' },
                { key: 'role' as const, label: 'Role', placeholder: 'e.g. Engineer, DevOps, PM, Student' },
                { key: 'company' as const, label: 'Company / Org', placeholder: 'Where do you work?' },
                { key: 'interest' as const, label: 'What brings you here?', placeholder: 'e.g. AI tooling, automation, governance' },
              ]).map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ display: 'block', color: '#8b949e', fontSize: 11, fontFamily: FONT, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
                  <input type="text" value={profile[key]} onChange={(e) => setProfile({ ...profile, [key]: e.target.value })} placeholder={placeholder}
                    style={{ width: '100%', padding: '10px 14px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, color: '#e6edf3', fontSize: 13, fontFamily: SANS, outline: 'none', transition: 'border-color 0.2s' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(79,143,255,0.3)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'} />
                </div>
              ))}
              <button onClick={handleSaveProfile} disabled={saving || saved}
                style={{ marginTop: 4, padding: '11px 20px', background: saved ? 'rgba(52,211,153,0.1)' : 'rgba(79,143,255,0.1)', border: `1px solid ${saved ? 'rgba(52,211,153,0.3)' : 'rgba(79,143,255,0.3)'}`, borderRadius: 8, color: saved ? GREEN : ACCENT, fontSize: 12, fontFamily: FONT, fontWeight: 500, cursor: saving ? 'wait' : 'pointer', transition: 'all 0.2s ease' }}>
                {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Profile'}
              </button>
              {saved && (
                <div style={{ marginTop: 16, padding: '16px 20px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ color: GREEN, fontSize: 14, fontWeight: 600, fontFamily: SANS, marginBottom: 6 }}>Your request has been received.</div>
                  <div style={{ color: '#8b949e', fontSize: 12, fontFamily: SANS, lineHeight: 1.6 }}>
                    Welcome to &gt; tool_os. We'll notify you when your workspace is ready. In the meantime, explore the <a href="https://pages.opentool.io" target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>docs</a> and <a href="https://pages.opentool.io/blog.html" target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>blog</a>.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: Links + Features */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Links */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '24px' }}>
              <h3 style={{ fontFamily: FONT, fontSize: 13, color: TEAL, margin: '0 0 16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Learn More</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { href: 'https://pages.opentool.io', label: 'Documentation', desc: 'Architecture, APIs, guides' },
                  { href: 'https://pages.opentool.io/blog.html', label: 'Blog', desc: 'Updates and case studies' },
                  { href: 'https://github.com/Opentool-io/opentool', label: 'GitHub', desc: 'Source code and issues' },
                ].map(({ href, label, desc }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 8, textDecoration: 'none', transition: 'border-color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(79,143,255,0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)'}>
                    <div>
                      <div style={{ color: '#e6edf3', fontSize: 13, fontFamily: SANS }}>{label}</div>
                      <div style={{ color: '#484f58', fontSize: 11, fontFamily: FONT }}>{desc}</div>
                    </div>
                    <span style={{ color: '#30363d', fontSize: 16 }}>&rarr;</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Features mini */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {FEATURES.map(({ icon, label }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 8, padding: '12px 8px', textAlign: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={icon} /></svg>
                  <div style={{ color: '#8b949e', fontSize: 9, fontFamily: FONT, marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ ORGANIZATIONS TABLE ═══ */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ fontFamily: FONT, fontSize: 13, color: ACCENT, margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organizations</h3>
            <span style={{ fontFamily: FONT, fontSize: 10, color: '#484f58' }}>{orgs.length} available</span>
          </div>

          {loadingOrgs ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#484f58', fontSize: 12, fontFamily: FONT }}>Loading...</div>
          ) : orgs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {orgs.map((org) => (
                <div key={org.id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 10, transition: 'border-color 0.2s, background 0.2s', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(79,143,255,0.2)'; e.currentTarget.style.background = 'rgba(79,143,255,0.03)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)'; e.currentTarget.style.background = 'rgba(0,0,0,0.2)'; }}
                  onClick={() => handleEnterOrg(org.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(79,143,255,0.1)', border: '1px solid rgba(79,143,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, fontSize: 13, fontWeight: 700, fontFamily: FONT }}>
                      {org.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ color: '#e6edf3', fontSize: 13, fontWeight: 600, fontFamily: SANS }}>{org.display_name}</div>
                      <div style={{ color: '#484f58', fontSize: 10, fontFamily: FONT }}>{org.slug} &middot; {org.role}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 9, fontFamily: FONT, color: GREEN, background: 'rgba(52,211,153,0.1)', padding: '2px 8px', borderRadius: 10 }}>{org.role}</span>
                    <span style={{ color: '#30363d', fontSize: 16 }}>&rarr;</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ color: '#484f58', fontSize: 12, fontFamily: FONT, marginBottom: 16 }}>No organizations yet</div>
              <p style={{ color: '#30363d', fontSize: 11, fontFamily: SANS, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
                Join an organization to access the full &gt; tool_os workspace. Paste an invite link below or ask a team member to invite you.
              </p>
            </div>
          )}

          {/* Invite link input */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <input type="text" value={inviteLink} onChange={(e) => setInviteLink(e.target.value)} placeholder="Paste invite link here..."
              onKeyDown={(e) => e.key === 'Enter' && handleJoinOrg()}
              style={{ flex: 1, padding: '10px 14px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, color: '#e6edf3', fontSize: 12, fontFamily: FONT, outline: 'none', transition: 'border-color 0.2s' }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(79,143,255,0.3)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'} />
            <button onClick={handleJoinOrg}
              style={{ padding: '10px 20px', background: 'rgba(79,143,255,0.1)', border: '1px solid rgba(79,143,255,0.3)', borderRadius: 8, color: ACCENT, fontSize: 12, fontFamily: FONT, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
              Join
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <p style={{ color: '#21262d', fontSize: 10, fontFamily: FONT, margin: 0 }}><span style={{ color: '#4f8fff', fontWeight: 700 }}>&gt;</span> <span style={{ color: '#e6edf3', fontWeight: 700 }}>tool</span><span style={{ color: '#4f8fff', fontWeight: 700 }}>_os</span> <span style={{ color: ACCENT, fontWeight: 700 }}>{__GIT_SHA__}</span></p>
        </div>
      </div>
    </div>
  );
}
